import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useLocation } from 'wouter';
import { Gift, CheckCircle, AlertCircle, Loader2, User, Mail, Calendar, CreditCard } from 'lucide-react';
import { API_BASE } from '../lib/api';
import AppLayout from '../components/AppLayout';

interface Voucher {
  id: string;
  voucherCode: string;
  recipientName: string;
  planDuration: number;
  planValue: number;
  status: string;
  paymentStatus: string;
  expiresAt: string;
  redeemedAt: string | null;
  personalMessage: string;
  isRedeemable: boolean;
  isExpired: boolean;
}

interface RedemptionState {
  step: 'input' | 'loading' | 'success' | 'error';
  voucher: Voucher | null;
  error: string | null;
  requiresAuth: boolean;
}

const GiftRedemptionPage = () => {
  const { getToken, isSignedIn } = useAuth();
  const [, navigate] = useLocation();
  const [location] = useLocation();

  const [voucherCode, setVoucherCode] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [redemptionState, setRedemptionState] = useState<RedemptionState>({
    step: 'input',
    voucher: null,
    error: null,
    requiresAuth: false
  });

  // Extract voucher code from URL if present
  useEffect(() => {
    const urlParams = new URLSearchParams(location.split('?')[1] || '');
    const codeFromUrl = urlParams.get('code');
    if (codeFromUrl) {
      setVoucherCode(codeFromUrl);
      // Auto-check voucher if code is in URL
      checkVoucher(codeFromUrl);
    }
  }, [location]);

  const checkVoucher = async (code: string = voucherCode) => {
    if (!code.trim()) return;

    setRedemptionState(prev => ({ ...prev, step: 'loading', error: null }));

    try {
      const response = await fetch(`${API_BASE}/gift-redeem/check?code=${encodeURIComponent(code.trim())}`);
      const data = await response.json();

      if (data.success) {
        setRedemptionState(prev => ({
          ...prev,
          step: 'input',
          voucher: data.voucher,
          error: null
        }));
      } else {
        setRedemptionState(prev => ({
          ...prev,
          step: 'error',
          error: data.message || 'Invalid voucher code'
        }));
      }
    } catch (error) {
      console.error('Error checking voucher:', error);
      setRedemptionState(prev => ({
        ...prev,
        step: 'error',
        error: 'Failed to check voucher. Please try again.'
      }));
    }
  };

  const redeemVoucher = async () => {
    if (!voucherCode.trim()) return;

    setRedemptionState(prev => ({ ...prev, step: 'loading', error: null }));

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Add auth token if user is signed in
      if (isSignedIn) {
        const token = await getToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      const response = await fetch(`${API_BASE}/gift-redeem`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          voucherCode: voucherCode.trim(),
          userEmail: userEmail.trim() || undefined
        }),
      });

      const data = await response.json();

      if (data.success) {
        setRedemptionState(prev => ({
          ...prev,
          step: 'success',
          voucher: data.voucher,
          requiresAuth: data.requiresAuthentication || false
        }));
      } else {
        setRedemptionState(prev => ({
          ...prev,
          step: 'error',
          error: data.message || 'Failed to redeem voucher'
        }));
      }
    } catch (error) {
      console.error('Error redeeming voucher:', error);
      setRedemptionState(prev => ({
        ...prev,
        step: 'error',
        error: 'Failed to redeem voucher. Please try again.'
      }));
    }
  };

  const handleCodeChange = (value: string) => {
    setVoucherCode(value);
    // Clear previous state when code changes
    if (redemptionState.voucher || redemptionState.error) {
      setRedemptionState(prev => ({
        ...prev,
        voucher: null,
        error: null,
        step: 'input'
      }));
    }
  };

  const formatVoucherCode = (code: string) => {
    // Format as XXXX-XXXX-XXXX
    const cleanCode = code.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    return cleanCode.replace(/(.{4})/g, '$1-').replace(/-$/, '');
  };

  const renderVoucherInput = () => (
    <div className="max-w-md mx-auto space-y-6">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
          <Gift className="h-4 w-4" />
          Redeem Gift Voucher
        </div>
        <h1 className="text-3xl font-display font-bold text-slate-900 mb-2">
          Welcome to HiPoAiCoach!
        </h1>
        <p className="text-slate-600">
          Enter your gift voucher code to start your coaching journey
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Gift Voucher Code
          </label>
          <input
            type="text"
            value={voucherCode}
            onChange={(e) => handleCodeChange(e.target.value)}
            onBlur={() => voucherCode && checkVoucher()}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-royal-blue focus:border-transparent text-center font-mono tracking-wider"
            placeholder="XXXX-XXXX-XXXX"
            maxLength={14}
          />
          <p className="text-xs text-slate-500 mt-1">
            Enter the code from your gift email
          </p>
        </div>

        {/* Show email input if not signed in */}
        {!isSignedIn && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              <Mail className="h-4 w-4 inline mr-1" />
              Your Email Address
            </label>
            <input
              type="email"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-royal-blue focus:border-transparent"
              placeholder="your@email.com"
              required={!isSignedIn}
            />
            <p className="text-xs text-slate-500 mt-1">
              This should match the email the gift was sent to
            </p>
          </div>
        )}
      </div>

      {/* Show voucher details if found */}
      {redemptionState.voucher && (
        <div className="bg-slate-50 rounded-2xl p-6 space-y-3">
          <div className="flex items-center gap-2 text-green-700 font-semibold">
            <CheckCircle className="h-5 w-5" />
            Valid Gift Voucher
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-slate-600">Recipient:</span>
              <p className="font-semibold">{redemptionState.voucher.recipientName}</p>
            </div>
            <div>
              <span className="text-slate-600">Duration:</span>
              <p className="font-semibold">{redemptionState.voucher.planDuration} months</p>
            </div>
            <div>
              <span className="text-slate-600">Value:</span>
              <p className="font-semibold text-royal-blue">₹{redemptionState.voucher.planValue}</p>
            </div>
            <div>
              <span className="text-slate-600">Expires:</span>
              <p className="font-semibold">
                {new Date(redemptionState.voucher.expiresAt).toLocaleDateString()}
              </p>
            </div>
          </div>

          {redemptionState.voucher.personalMessage && (
            <div>
              <span className="text-slate-600 text-sm">Personal Message:</span>
              <p className="text-sm bg-white p-3 rounded-lg italic mt-1">
                "{redemptionState.voucher.personalMessage}"
              </p>
            </div>
          )}

          {!redemptionState.voucher.isRedeemable && (
            <div className="flex items-center gap-2 text-amber-700 bg-amber-50 p-3 rounded-lg">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">
                {redemptionState.voucher.isExpired
                  ? 'This voucher has expired'
                  : 'This voucher cannot be redeemed'
                }
              </span>
            </div>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div className="space-y-3">
        {redemptionState.voucher?.isRedeemable ? (
          <button
            onClick={redeemVoucher}
            disabled={redemptionState.step === 'loading'}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-semibold"
          >
            {redemptionState.step === 'loading' ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Redeeming...
              </>
            ) : (
              <>
                <Gift className="h-4 w-4" />
                Redeem Gift
              </>
            )}
          </button>
        ) : voucherCode && !redemptionState.voucher ? (
          <button
            onClick={() => checkVoucher()}
            disabled={redemptionState.step === 'loading'}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-royal-blue text-white rounded-lg hover:bg-royal-blue/90 disabled:opacity-50 font-semibold"
          >
            {redemptionState.step === 'loading' ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Checking...
              </>
            ) : (
              'Check Voucher'
            )}
          </button>
        ) : null}

        {!isSignedIn && (
          <div className="text-center">
            <p className="text-sm text-slate-600 mb-2">
              Already have an account?
            </p>
            <button
              onClick={() => navigate('/signin')}
              className="text-royal-blue hover:text-royal-blue/80 font-semibold"
            >
              Sign In Here
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderSuccess = () => (
    <div className="max-w-md mx-auto text-center space-y-6">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
          <CheckCircle className="h-8 w-8 text-green-600" />
        </div>
        <h1 className="text-3xl font-display font-bold text-slate-900 mb-2">
          Gift Redeemed Successfully! 🎉
        </h1>
        <p className="text-slate-600">
          Your {redemptionState.voucher?.planDuration}-month coaching subscription is now active
        </p>
      </div>

      <div className="bg-green-50 rounded-2xl p-6 space-y-3">
        <h3 className="font-semibold text-green-800">What's Next?</h3>
        <ul className="text-sm text-green-700 space-y-2">
          <li className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Access to all 6 AI coaches
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Personalized coaching conversations
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Minutes of Meeting after each session
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Growth insights and analytics
          </li>
        </ul>
      </div>

      <div className="space-y-3">
        {redemptionState.requiresAuth ? (
          <button
            onClick={() => navigate('/signup')}
            className="w-full px-6 py-3 bg-royal-blue text-white rounded-lg hover:bg-royal-blue/90 font-semibold"
          >
            Create Your Account
          </button>
        ) : (
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full px-6 py-3 bg-royal-blue text-white rounded-lg hover:bg-royal-blue/90 font-semibold"
          >
            Start Coaching Journey
          </button>
        )}

        <button
          onClick={() => navigate('/')}
          className="w-full px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 font-semibold"
        >
          Learn More About HiPoAiCoach
        </button>
      </div>
    </div>
  );

  const renderError = () => (
    <div className="max-w-md mx-auto text-center space-y-6">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
          <AlertCircle className="h-8 w-8 text-red-600" />
        </div>
        <h1 className="text-2xl font-display font-bold text-slate-900 mb-2">
          Unable to Redeem Gift
        </h1>
        <p className="text-slate-600 mb-4">
          {redemptionState.error}
        </p>
      </div>

      <div className="bg-slate-50 rounded-2xl p-6 space-y-3 text-left">
        <h3 className="font-semibold text-slate-800">Common Issues:</h3>
        <ul className="text-sm text-slate-600 space-y-2">
          <li>• Double-check the voucher code for typos</li>
          <li>• Ensure the email matches the gift recipient</li>
          <li>• Check if the voucher has expired</li>
          <li>• Verify the voucher hasn't already been redeemed</li>
        </ul>
      </div>

      <button
        onClick={() => setRedemptionState({ step: 'input', voucher: null, error: null, requiresAuth: false })}
        className="w-full px-6 py-3 bg-royal-blue text-white rounded-lg hover:bg-royal-blue/90 font-semibold"
      >
        Try Again
      </button>
    </div>
  );

  const renderContent = () => {
    switch (redemptionState.step) {
      case 'success':
        return renderSuccess();
      case 'error':
        return renderError();
      case 'loading':
      case 'input':
      default:
        return renderVoucherInput();
    }
  };

  return (
    <AppLayout currentPage="home">
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-white py-12">
        <div className="max-w-2xl mx-auto px-4">
          {renderContent()}
        </div>
      </div>
    </AppLayout>
  );
};

export default GiftRedemptionPage;
